import { useState } from 'react';
import { pbToast } from '../toast';
import { FormFields, FormFooter, initialValuesFromFields } from './FormFields';
import { G_DISC, ORBIT1_R } from '../constants';
import type { FieldSpec } from './FormFields';
import type { OrgChartDoc, Group } from '../types';
import type { GroupEntry } from '../layout';

function buildFields(doc: OrgChartDoc, layout: GroupEntry[], gi: number | null | undefined): FieldSpec[] {
  const isNew = gi == null;
  const gr = (isNew ? {} : doc.groups[gi]) as Partial<Group>;
  const ge = isNew ? null : layout[gi];
  const solo = !!(ge && ge.solo);
  const soloProg = solo ? (gr.programs || [])[0] : null;
  const settings = doc.settings;

  const fields: FieldSpec[] = [
    { key: 'id', label: 'Code', type: 'text', value: gr.id || '', placeholder: 'DP', half: true },
    {
      key: 'short', label: 'Short name', type: 'text', value: gr.short || '',
      placeholder: 'Dedicated Programs', half: true,
    },
    {
      key: 'label', label: 'Disc label', type: 'textarea', value: (gr.label || '').replace(/\\n/g, '\n'),
      hint: 'Press Enter for a second line on the disc.',
    },
    {
      key: 'task', label: 'Badge text', type: 'text', value: gr.task || '', placeholder: 'T6',
      hint: 'Blank = total FTE count.',
    },
    { key: 'color', label: 'Accent colour', type: 'color', value: gr.color || settings.teal },

    { section: 'Geometry' },
    {
      key: 'discR', label: 'Disc radius', type: 'number', half: true, min: 30, max: 340,
      value: ge ? Math.round(ge.discR) : G_DISC,
    },
    {
      key: 'ringR', label: 'People orbit radius', type: 'number', half: true, min: 40, max: 520,
      value: solo ? Math.round(ge!.progs[0].ring) : (gr.ringR || ORBIT1_R),
      hint: 'Applies when the group holds a single program.',
    },
    {
      key: 'x', label: 'X position', type: 'number', half: true,
      value: ge ? Math.round(ge.gcx) : Math.round(doc.sun.x + 560),
    },
    {
      key: 'y', label: 'Y position', type: 'number', half: true,
      value: ge ? Math.round(ge.gcy) : Math.round(doc.sun.y - 400),
    },

    { section: 'Disc styling' },
    { key: 'fill', label: 'Disc background', type: 'color', half: true, value: gr.fill || settings.groupFill },
    {
      key: 'textColor', label: 'Disc text colour', type: 'color', half: true,
      value: gr.textColor || settings.groupText,
    },
    {
      key: 'fontSize', label: 'Disc text size px', type: 'number', half: true, min: 6, max: 44, step: 0.5,
      value: gr.fontSize || settings.groupFont,
    },
    { key: 'badgeColor', label: 'Badge colour', type: 'color', half: true, value: gr.badgeColor || '#5A6A7C' },

    { section: 'Orbit ring' },
    { key: 'ringShow', label: 'Show this orbit ring', type: 'checkbox', value: gr.ringShow !== false },
    {
      key: 'ringColor', label: 'Ring colour', type: 'color', half: true,
      value: gr.ringColor || gr.color || settings.teal,
    },
    {
      key: 'ringWidth', label: 'Ring thickness', type: 'number', half: true, min: 0.4, max: 14, step: 0.2,
      value: gr.ringWidth != null ? gr.ringWidth : settings.ringWidth,
    },
    {
      key: 'ringDash', label: 'Ring dash pattern', type: 'text', half: true, placeholder: '6 5',
      value: gr.ringDash || settings.ringDash,
    },
    {
      key: 'ringSpeed', label: 'Rotation seconds / cycle', type: 'number', half: true, min: 1, max: 200,
      value: gr.ringSpeed != null ? gr.ringSpeed : settings.ringSpeed,
    },
    {
      key: 'ringSpin', label: 'Rotation direction', type: 'select', half: true,
      value: gr.ringSpin || settings.ringSpin,
      options: [
        { value: 'normal', label: 'Clockwise' },
        { value: 'reverse', label: 'Counter-clockwise' },
        { value: 'off', label: 'No rotation' },
      ],
    },

    { section: 'Connector line (from the centre disc)' },
    { key: 'wireShow', label: 'Show this connector', type: 'checkbox', value: gr.wireShow !== false },
    { key: 'wireColor', label: 'Line colour', type: 'color', half: true, value: gr.wireColor || settings.wireColor },
    {
      key: 'wireWidth', label: 'Line thickness', type: 'number', half: true, min: 0.4, max: 14, step: 0.2,
      value: gr.wireWidth != null ? gr.wireWidth : settings.wireWidth,
    },
    {
      key: 'wireDash', label: 'Line dash pattern', type: 'text', half: true, placeholder: 'solid',
      value: gr.wireDash || '',
    },
    {
      key: 'wireArrow', label: 'Arrowheads', type: 'select', half: true,
      value: gr.wireArrow || settings.wireArrow,
      options: [
        { value: 'none', label: 'None' },
        { value: 'end', label: 'Arrow at the group' },
        { value: 'start', label: 'Arrow at the centre' },
        { value: 'both', label: 'Both ends' },
      ],
    },
  ];

  if (solo && soloProg) {
    fields.push(
      { section: 'Program record (single-program group)' },
      { key: 'pid', label: 'Program code', type: 'text', half: true, value: soloProg.id || '' },
      { key: 'pshort', label: 'Program short name', type: 'text', half: true, value: soloProg.short || '' },
      {
        key: 'slotRot', label: 'People fan rotation °', type: 'number', min: -360, max: 360, step: 5,
        value: soloProg.slotRot || 0,
      },
    );
  }

  return fields;
}

interface GroupFormValues {
  id: string;
  short: string;
  label: string;
  task: string;
  color: string;
  discR: number | null;
  ringR: number | null;
  x: number | null;
  y: number | null;
  fill: string;
  textColor: string;
  fontSize: number | null;
  badgeColor: string;
  ringShow: boolean;
  ringColor: string;
  ringWidth: number | null;
  ringDash: string;
  ringSpeed: number | null;
  ringSpin: string;
  wireShow: boolean;
  wireColor: string;
  wireWidth: number | null;
  wireDash: string;
  wireArrow: string;
  pid?: string;
  pshort?: string;
  slotRot?: number | null;
}

function validate(values: GroupFormValues): string[] {
  const errors = [];
  if (!values.short.trim() && !values.label.trim()) errors.push('Give the group a name.');
  return errors;
}

interface GroupEditorProps {
  doc: OrgChartDoc;
  layout: GroupEntry[];
  gi: number | null | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  dispatch: (action: any) => void;
  closeModal: () => void;
  confirm: (message: string) => boolean;
}

export default function GroupEditor({ doc, layout, gi, dispatch, closeModal, confirm }: GroupEditorProps) {
  const isNew = gi == null;
  const gr = (isNew ? {} : doc.groups[gi]) as Partial<Group>;
  const ge = isNew ? null : layout[gi as number];
  const solo = !!(ge && ge.solo);

  const [values, setValues] = useState(
    () => initialValuesFromFields(buildFields(doc, layout, gi)) as unknown as GroupFormValues,
  );
  const onChange = (key: string, value: boolean | number | null | string) =>
    setValues((v) => ({ ...v, [key]: value } as unknown as GroupFormValues));

  const onSave = () => {
    const errors = validate(values);
    if (errors.length) {
      pbToast(errors.join('\n'), 'err');
      return;
    }

    const id = values.id.trim() || (values.short.trim().slice(0, 5).toUpperCase() || 'NEW');
    const short = values.short.trim() || values.label.trim().replace(/\n/g, ' ');
    const label = values.label.trim() || short;

    const patch: Record<string, unknown> = {
      id, short, label,
      task: values.task.trim() ? values.task.trim() : undefined,
      color: values.color,
      discR: values.discR, ringR: values.ringR, x: values.x, y: values.y,
      fill: values.fill, textColor: values.textColor, fontSize: values.fontSize,
      badgeColor: values.badgeColor,
      ringShow: values.ringShow, ringColor: values.ringColor, ringWidth: values.ringWidth,
      ringDash: values.ringDash, ringSpeed: values.ringSpeed, ringSpin: values.ringSpin,
      wireShow: values.wireShow, wireColor: values.wireColor, wireWidth: values.wireWidth,
      wireDash: values.wireDash, wireArrow: values.wireArrow,
    };

    if (isNew) {
      dispatch({ type: 'ADD_GROUP', group: patch });
      pbToast(`Added group ${short}`, 'ok');
    } else {
      dispatch({ type: 'UPDATE_GROUP', gi, patch });
      if (solo) {
        const pid = values.pid as string;
        const pshort = values.pshort as string;
        const slotRot = values.slotRot as number | null;
        const soloProgram = (gr.programs || [])[0];
        dispatch({
          type: 'UPDATE_PROGRAM',
          gi,
          pi: 0,
          patch: {
            id: pid.trim() || soloProgram.id,
            short: pshort.trim() || soloProgram.short,
            slotRot: slotRot || 0,
          },
        });
      }
      pbToast('Group updated', 'ok');
    }
    closeModal();
  };

  const onDelete = () => {
    const groupPrograms = gr.programs || [];
    const peopleCount = groupPrograms.reduce((a, p) => a + p.people.length, 0);
    const message = `Delete the group "${gr.short}", its ${groupPrograms.length} program(s) and ${peopleCount} people?`;
    if (!confirm(message)) return;
    dispatch({ type: 'DELETE_GROUP', gi });
    pbToast(`Deleted group ${gr.short}`, 'ok');
    closeModal();
  };

  return (
    <>
      <p style={{ margin: '0 0 16px', fontSize: 12, color: 'var(--mute2)', lineHeight: 1.5 }}>
        {isNew ? 'A new tier-1 orbit around the program centre.' : gr.short}
      </p>
      <FormFields fields={buildFields(doc, layout, gi)} values={values as unknown as Record<string, boolean | number | null | string>} onChange={onChange} idPrefix="group-" />
      <FormFooter
        saveLabel={isNew ? 'Add group' : 'Save changes'}
        deleteLabel="Delete group"
        onCancel={() => closeModal()}
        onDelete={isNew ? undefined : onDelete}
        onSave={onSave}
      />
    </>
  );
}
